import React, { useState, useEffect, useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box,
  Container,
  Typography,
  Chip,
  Grid,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Modal,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded'

import Header from '../components/Headers/Header'
import OrderCard, { isRefundedOrder, orderTotal } from '../components/Order/OrderCard'
import OrdersTable from '../components/Order/OrdersTable'
import OrderComponent from '../components/Order/Order'
import EmailOrderCard, {
  isScheduledCourierOrder,
  courierPrintDueAt
} from '../components/Order/EmailOrderCard'
import EmailOrderDetail from '../components/Order/EmailOrderDetail'
import EhungryOrderCard, {
  isScheduledEhungryOrder,
  ehungryDueAt
} from '../components/Order/EhungryOrderCard'
import EhungryOrderDetail from '../components/Order/EhungryOrderDetail'
import {
  ops, opsCssVars, FS, RADIUS, SHADOW, EASE, tabular, labelSx, focusRing, noMotion, btn,
  tone, ORDER_TONE, fadeUp, pulse, arrivalGlow, modalIn, dialogPaperSx, backdropSx
} from '../components/Order/opsTokens'
import { FilterPill, ScrollRow, Segmented, OrderCardSkeleton, EmptyState } from '../components/Order/OpsUi'
import { inDateFilter, orderZone } from '../utils/restaurantTime'

import {
  getActiveOrders,
  getScheduledOrders,
  getOrdersByRestaurant,
  getOrders,
  subscribePlaceOrder,
  orderCount,
  getAvailableRiders,
  updateStatus,
  markOrderPrepared,
  updateOrderStatus,
  assignRider,
  getConfiguration,
  getRestaurantStaff,
  getOnDutyStaff,
  setOnDutyStaff
} from '../apollo'

const GET_ACTIVE_ORDERS = gql`${getActiveOrders}`
const GET_SCHEDULED_ORDERS = gql`${getScheduledOrders}`
const GET_ORDERS_BY_RESTAURANT = gql`${getOrdersByRestaurant}`
const GET_ALL_ORDERS = gql`${getOrders}`
const SUBSCRIBE_PLACE_ORDER = gql`${subscribePlaceOrder}`
const ORDER_COUNT = gql`${orderCount}`
const GET_AVAILABLE_RIDERS = gql`${getAvailableRiders}`
const UPDATE_STATUS = gql`${updateStatus}`
const MARK_PREPARED = gql`${markOrderPrepared}`
const UPDATE_ORDER_STATUS = gql`${updateOrderStatus}`
const GET_RESTAURANT_STAFF = gql`${getRestaurantStaff}`
const GET_ON_DUTY_STAFF = gql`${getOnDutyStaff}`
const SET_ON_DUTY_STAFF = gql`${setOnDutyStaff}`
const ASSIGN_RIDER = gql`${assignRider}`
const GET_CONFIGURATION = gql`${getConfiguration}`
// Field lists shared by the restaurant-scoped queries and the super admin's
// admin-wide ones, so both views render the same cards.
const COURIER_ORDER_LIST = `
      total
      orders {
        _id externalOrderNumber orderKind status
        customerName customerPhone
        dropoffAddress promiseTime
        subtotal deliveryFee processingFee tax tip total
        isPaid paymentMethod
        fdOrderId fdDriverName lastFdEventAt failureReason
        # Which partner system the email came from (MENUSIFU/EDINING/OTHER) —
        # drives the source chip so staff know whose screen this order is also on.
        merchantName merchantProvider
        # The rest of what First Delivery reports. The database has carried all
        # of it since the start; this page simply never asked, so an email order
        # showed a driver's name and nothing else — no ETA, no tracking, and no
        # photo proving it was dropped off.
        fdDriverPhone fdDriverTransport fdDspName
        fdTrackerLink fdDspTrackerLink
        fdPickupEta fdDropoffEta fdDropoffPhoto
        createdAt
        items { _id position title quantity unitPrice modifiers }
      }
`
const RESTAURANT_COURIER_ORDERS = gql`
  query RestaurantCourierOrders($restaurantId: ID) {
    restaurantCourierOrders(restaurantId: $restaurantId) {${COURIER_ORDER_LIST}    }
  }
`
// Super admin (no restaurant selected): every partner restaurant's email orders.
const ADMIN_COURIER_ORDERS = gql`
  query AdminCourierOrders {
    courierOrders(limit: 2000) {${COURIER_ORDER_LIST}    }
  }
`
// Emails that never became a CourierOrder — mainly pickup orders. classify.js
// requires 2 agreeing signals before anything auto-imports, and MenuSifu's
// pickup template only ever carries one (see classify.js/menusifu.js), so a
// pickup order sits here forever unless a human force-imports it. Without
// this, "Email Orders" would only ever show deliveries.
//
// Read-only, same as restaurantCourierOrders above — this never creates a
// CourierOrder row and never touches dispatch. A pickup order shown from here
// has no order id and nothing to dispatch; dispatch.js's own first guard also
// refuses anything that isn't orderKind DELIVERY regardless.
const RESTAURANT_COURIER_EMAILS_PENDING = gql`
  query RestaurantCourierEmailsPending($restaurantId: ID) {
    restaurantCourierEmails(restaurantId: $restaurantId, limit: 200) {
      emails { _id status parsed courierOrderId reviewReason error createdAt merchantName }
    }
  }
`
const ADMIN_COURIER_EMAILS_PENDING = gql`
  query AdminCourierEmailsPending {
    courierEmails(limit: 2000) {
      emails { _id status parsed courierOrderId reviewReason error createdAt merchantName }
    }
  }
`
// eHungry orders mirrored from the legacy RushMyFood app. Restaurant-scoped and
// read-only except for the status, which writes through to that app.
const EHUNGRY_ORDER_LIST = `
      total
      orders {
        _id remoteId externalId status orderType
        placedAt readyAt processedAt
        restaurantName customerName customerPhone
        deliveryAddress deliveryNote notesToDriver specialInstructions
        total subtotal tax taxLabel tip deliveryCharge additionalFee
        couponAmount couponName discount paymentDescription
        assignedCourierName
        items { name variant category quantity unitPrice totalPrice specialInstructions forGuest addons { name choice portion quantity price totalPrice } }
        courier {
          provider status fee
          assignedAt acceptedAt arrivedOriginAt pickedUpAt arrivedDropoffAt droppedOffAt cancelledAt
          pickupEstimate dropoffEstimate driverName driverPhone driverPhoto driverTransport trackerLink dspTrackerLink dropoffPhoto
        }
      }
`
const EHUNGRY_ORDERS = gql`
  query EhungryOrders($restaurantId: ID) {
    ehungryOrders(restaurantId: $restaurantId, limit: 200) {${EHUNGRY_ORDER_LIST}    }
  }
`
// Super admin: every linked location's legacy-app orders.
const ADMIN_EHUNGRY_ORDERS = gql`
  query AdminEhungryOrdersForOrders {
    adminEhungryOrders(limit: 2000) {${EHUNGRY_ORDER_LIST}    }
  }
`
const EHUNGRY_UPDATE_STATUS = gql`
  mutation EhungryUpdateOrderStatus($id: ID!, $status: String!) {
    ehungryUpdateOrderStatus(id: $id, status: $status) {
      _id status processedAt
    }
  }
`

const RESTAURANT_MARK_PICKUP_COMPLETE = gql`
  mutation RestaurantMarkCourierPickupComplete($orderId: ID, $emailId: ID, $restaurantId: ID, $markPaid: Boolean) {
    restaurantMarkCourierPickupComplete(orderId: $orderId, emailId: $emailId, restaurantId: $restaurantId, markPaid: $markPaid) {
      _id status isPaid
    }
  }
`

// Status tabs for the live view filter
const STATUS_TABS = [
  { value: 'ALL', label: 'All Orders' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'PENDING', label: 'New Orders' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'PREPARED', label: 'Ready' },
  { value: 'ASSIGNED', label: 'Rider Out' },
  { value: 'PICKED', label: 'On The Way' },
  { value: 'DELIVERED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REFUNDED', label: 'Refunded' },
]

/* Where an order came from — a second filter that combines with the status
   pills (RushMyFood + Accepted, Email + Completed…).
   MARKETPLACE: orders placed through RushMyFood's own apps and site.
   EMAIL: courier orders parsed from a partner platform's emails (their own table).
   EHUNGRY: mirrored from the legacy RushMyFood app (ehungry_orders). */
const SOURCE_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'MARKETPLACE', label: 'RushMyFood' },
  { value: 'EMAIL', label: 'Email Orders' },
  { value: 'EHUNGRY', label: 'RushMyFood (old)' },
]

const DATE_FILTERS = [
  { value: 'TODAY', label: 'Today' },
  { value: 'YESTERDAY', label: 'Yesterday' },
  { value: 'LAST_WEEK', label: 'Last Week' },
  { value: 'THIS_MONTH', label: 'This Month' },
  { value: 'ALL', label: 'All Time' },
  { value: 'CUSTOM', label: 'Custom' }
]

// Delivery or pickup across all three sources (see fulfilmentOf), plus the two
// kinds of order a restaurant takes itself: on the POS, and over the phone.
const TYPE_FILTERS = [
  { value: 'ALL', label: 'All types' },
  { value: 'DELIVERY', label: 'Delivery' },
  { value: 'PICKUP', label: 'Pickup' },
  { value: 'IN_STORE', label: 'In-store (POS)' },
  { value: 'PHONE', label: 'Phone orders' }
]

/* Delivery, pickup, or neither (dine-in, counter): a partner email order says
   it in orderKind, an old-app (eHungry) order in orderType, and a marketplace
   order in isPickedUp / orderType. */
const fulfilmentOf = o => {
  if (o.__courier) {
    const k = String(o.orderKind || '').toUpperCase()
    return k === 'DELIVERY' ? 'DELIVERY' : k === 'PICKUP' || k === 'TAKEOUT' ? 'PICKUP' : 'OTHER'
  }
  if (o.__ehungry) {
    const t = String(o.orderType || '').toUpperCase()
    return t === 'DELIVERY' ? 'DELIVERY' : t === 'PICKUP' ? 'PICKUP' : 'OTHER'
  }
  if (String(o.orderType || '').startsWith('POS_')) return 'IN_STORE'
  if (o.isPickedUp === true || Number(o.isPickedUp) === 1) return 'PICKUP'
  return 'DELIVERY'
}

const SORTS = [
  { value: 'NEWEST', label: 'Newest first' },
  { value: 'OLDEST', label: 'Oldest first' },
  { value: 'TOTAL_DESC', label: 'Highest total' },
  { value: 'TOTAL_ASC', label: 'Lowest total' }
]

const SOURCE_ICONS = {
  MARKETPLACE: <ShoppingBagOutlinedIcon />,
  EMAIL: <MailOutlineIcon />,
  EHUNGRY: <StorefrontOutlinedIcon />
}

// Cards: 1 column on a phone, 2 on a tablet, 3–4 on a desktop.
const cardGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
  gap: 2,
  alignItems: 'stretch'
}

// Each grid cell fades up on mount, 30ms apart, capped at 300ms.
const cellSx = i => ({
  position: 'relative',
  minWidth: 0,
  borderRadius: RADIUS.card,
  animation: `${fadeUp} 260ms ${EASE} both`,
  animationDelay: `${Math.min(i, 10) * 30}ms`,
  ...noMotion
})

// Drawn over a card that arrived by live update after the page settled.
const arrivalSx = {
  position: 'absolute',
  inset: 0,
  borderRadius: RADIUS.card,
  pointerEvents: 'none',
  animation: `${arrivalGlow} 2400ms ease-out 1`,
  ...noMotion
}

const SkeletonGrid = () => (
  <Box sx={cardGridSx} aria-busy="true" aria-label="Loading orders">
    {Array.from({ length: 8 }).map((_, i) => <OrderCardSkeleton key={i} />)}
  </Box>
)

// `range` is { from, to } as YYYY-MM-DD, used only by CUSTOM; either end may be empty.
// Days are the restaurant's own (utils/restaurantTime.js), so "Today" for a
// Central restaurant runs midnight to midnight Central, wherever you are.
const filterByDate = (orders, dateFilter, range) => {
  if (!orders) return []
  if (dateFilter === 'ALL') return orders
  const now = new Date()
  return orders.filter(o => inDateFilter(o.createdAt, orderZone(o), dateFilter, range, now))
}

const filterByStatus = (orders, statusFilter) => {
  if (!orders) return []
  if (statusFilter === 'ALL') return orders
  if (statusFilter === 'REFUNDED') return orders.filter(isRefundedOrder)
  return orders.filter(o => o.orderStatus === statusFilter)
}

/* What the search box looks through, per kind of order: its number, the
   customer's name and phone, and the restaurant. */
const searchFields = o =>
  o.__ehungry
    ? [o.remoteId, o.externalId, o.customerName, o.customerPhone, o.restaurantName]
    : o.__courier
      ? [o.externalOrderNumber, o._id, o.customerName, o.customerPhone, o.merchantName]
      : [o.orderId, o.user?.name, o.user?.phone, o.restaurant?.name]

/* Every word typed must match somewhere. Short numbers match order numbers only
   ("43" finds ORD-00043); four or more digits also match phone numbers. */
const buildMatcher = text => {
  const words = String(text || '').toLowerCase().replace(/#/g, ' ').split(/\s+/).filter(Boolean)
  if (!words.length) return null
  return o => {
    const fields = searchFields(o)
    const hay = fields.map(f => String(f ?? '').toLowerCase()).join(' | ')
    const ids = [fields[0], fields[1]].map(f => String(f ?? '').replace(/\D/g, '')).join(' ')
    const allDigits = fields.map(f => String(f ?? '').replace(/\D/g, '')).join(' ')
    return words.every(w => hay.includes(w) || (/^\d+$/.test(w) && (w.length >= 4 ? allDigits : ids).includes(w)))
  }
}

// The total a card shows, so "Highest total" sorts the way the cards read.
const cardTotal = o =>
  o.__ehungry || o.__courier
    ? Number(o.total || 0)
    : orderTotal(o)

const sortOrders = (list, sortBy) => {
  const t = o => new Date(o.createdAt).getTime() || 0
  const by = {
    NEWEST: (a, b) => t(b) - t(a),
    OLDEST: (a, b) => t(a) - t(b),
    TOTAL_DESC: (a, b) => cardTotal(b) - cardTotal(a),
    TOTAL_ASC: (a, b) => cardTotal(a) - cardTotal(b)
  }[sortBy] || ((a, b) => t(b) - t(a))
  return list.slice().sort(by)
}

// A CourierEmail that never became a CourierOrder — reshaped into the same
// view-model EmailOrderCard/EmailOrderDetail already read, off its parsed JSON
// rather than real order columns (there is no order row to read from). No FD
// fields: nothing here has ever been dispatched, since nothing here has an
// order id for dispatch.js to act on in the first place.
//
// No merchantProvider either, deliberately: these are largely here BECAUSE no
// merchant matched, so nothing can honestly say whose system the order came
// from. They keep the plain envelope chip rather than being stamped with a
// brand on a guess.
const normalizePendingEmail = (email) => {
  let parsed = null
  try { parsed = email.parsed ? JSON.parse(email.parsed) : null } catch { parsed = null }
  if (!parsed) return null
  return {
    _id: `email-${email._id}`,
    externalOrderNumber: parsed.externalOrderNumber,
    status: email.status,
    // The matched merchant when there is one, else the name printed at the top
    // of the email itself — a name, unlike a provider brand, is on the receipt.
    merchantName: email.merchantName || parsed.restaurantNameRaw || null,
    customerName: parsed.customer?.name,
    customerPhone: parsed.customer?.phone,
    items: (parsed.items || []).map((it, i) => ({
      _id: `${email._id}-${i}`, title: it.title, quantity: it.quantity, unitPrice: it.unitPrice,
    })),
    total: parsed.money?.total,
    subtotal: parsed.money?.subTotal,
    deliveryFee: parsed.money?.deliveryFee,
    processingFee: parsed.money?.processingFee,
    tax: parsed.money?.tax,
    tip: parsed.money?.tip,
    orderKind: parsed.orderKind,
    dropoffAddress: parsed.address?.raw,
    // The resolved, timezone-aware Date — not parsed.promiseTimeRaw (a display
    // string like "08:05-8:20 PM Aug 25(ASAP)"). Promise-time resolution in
    // validate.js runs unconditionally, independent of the classification
    // blocker that's the whole reason this email is still just pending, so
    // this is populated even for a NEEDS_REVIEW single-signal pickup order.
    promiseTime: parsed._validation?.resolved?.promiseTime || null,
    // Tri-state, same as the model column: null means the receipt had no
    // payment-status line at all, not the same fact as unpaid.
    isPaid: parsed.isPaid ?? null,
    paymentMethod: parsed.paymentMethodRaw || null,
    fdOrderId: null,
    fdDriverName: null,
    lastFdEventAt: null,
    failureReason: email.reviewReason || email.error || null,
    createdAt: email.createdAt,
  }
}

const Orders = () => {
  // Auth context
  const restaurantId = localStorage.getItem('restaurantId')
  const isSuperAdmin = !restaurantId

  // UI state
  const [viewMode, setViewMode] = useState('live')
  const [statusFilter, setStatusFilter] = useState('ALL')
  // Where an order came from; combines with statusFilter (see SOURCE_TABS).
  const [sourceFilter, setSourceFilter] = useState('ALL')
  const [sortBy, setSortBy] = useState('NEWEST')
  // The live view's search box. Not `search` below, which is ordersByRestId's
  // server-side search variable — typing here must never refetch.
  const [orderSearch, setOrderSearch] = useState('')
  const [customRange, setCustomRange] = useState({ from: '', to: '' })
  const [dateFilter, setDateFilter] = useState('TODAY')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [detailsModal, setDetailModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [selectedEmailOrder, setSelectedEmailOrder] = useState(null)
  const [selectedEhungryOrder, setSelectedEhungryOrder] = useState(null)
  const [ehungryError, setEhungryError] = useState(null)

  const [search] = useState('')

  // Rider assignment dialog
  const [riderDialog, setRiderDialog] = useState(false)
  const [assigningOrder, setAssigningOrder] = useState(null)
  const [selectedRider, setSelectedRider] = useState('')

  // Cancel dialog
  const [cancelDialog, setCancelDialog] = useState(false)
  const [cancellingOrder, setCancellingOrder] = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelComments, setCancelComments] = useState('')

  // Feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  // On Duty panel
  const [onDutyPanelOpen, setOnDutyPanelOpen] = useState(false)
  const [selectedOnDutyIds, setSelectedOnDutyIds] = useState([])

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity })
  }

  // ── Queries ───────────────────────────────────────────────────────
  const { data: configData } = useQuery(GET_CONFIGURATION)
  const currencySymbol = configData?.configuration?.currencySymbol || '$'

  const {
    data: activeOrdersData,
    loading: activeLoading,
    refetch: refetchActive,
    subscribeToMore: activeSubscribeToMore
  } = useQuery(GET_ACTIVE_ORDERS, {
    variables: { restaurantId: restaurantId || null },
    // Poll every 30s for super admin (no per-restaurant subscription available)
    pollInterval: isSuperAdmin ? 30000 : 0
  })

  const {
    data: scheduledOrdersData,
    refetch: refetchScheduled
  } = useQuery(GET_SCHEDULED_ORDERS, {
    skip: isSuperAdmin,
    variables: { restaurantId: restaurantId || null },
    pollInterval: 60000
  })

  const {
    data: restData,
    subscribeToMore: restSubscribeToMore
  } = useQuery(GET_ORDERS_BY_RESTAURANT, {
    skip: isSuperAdmin,
    // Fetch all orders — table view paginates client-side, stats need the full dataset
    variables: {
      restaurant: restaurantId,
      page: 0,
      rows: 9999,
      search
    }
  })

  const {
    data: allData,
    loading: allLoading,
    refetch: refetchAll,
    subscribeToMore: allSubscribeToMore
  } = useQuery(GET_ALL_ORDERS, {
    skip: !isSuperAdmin,
    // Every order (the list filters and pages client-side, like the restaurant
    // view). allOrders was 20 rows, so the screen showed only the newest 20.
    variables: { page: 0, rows: 2000 }
  })

  useQuery(ORDER_COUNT, {
    variables: { restaurant: restaurantId }
  })

  const { data: ridersData } = useQuery(GET_AVAILABLE_RIDERS)

  // Not available in a cross-restaurant super-admin view — restaurantCourierOrders
  // needs one specific restaurant, resolved from ctx.restaurantId when omitted.
  const { data: courierOrdersData, loading: courierOrdersLoading, refetch: refetchCourierOrders } = useQuery(RESTAURANT_COURIER_ORDERS, {
    skip: isSuperAdmin,
    variables: { restaurantId: restaurantId || null },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
  })
  const { data: courierEmailsData, refetch: refetchCourierEmails } = useQuery(RESTAURANT_COURIER_EMAILS_PENDING, {
    skip: isSuperAdmin,
    variables: { restaurantId: restaurantId || null },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
  })
  // Same restriction as the courier queries above: ehungryOrders resolves one
  // specific restaurant, so it has nothing to answer in the cross-restaurant
  // super-admin view (which reads the Dispatch board's eHungry tab instead).
  const { data: ehungryData, loading: ehungryLoading, refetch: refetchEhungry } = useQuery(EHUNGRY_ORDERS, {
    skip: isSuperAdmin,
    variables: { restaurantId: restaurantId || null },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
  })
  // The super admin reads the same three lists platform-wide (admin-only queries).
  const adminLists = { skip: !isSuperAdmin, fetchPolicy: 'cache-and-network', pollInterval: 60000 }
  const { data: adminCourierData, loading: adminCourierLoading, refetch: refetchAdminCourierOrders } = useQuery(ADMIN_COURIER_ORDERS, adminLists)
  const { data: adminCourierEmailsData, refetch: refetchAdminCourierEmails } = useQuery(ADMIN_COURIER_EMAILS_PENDING, adminLists)
  const { data: adminEhungryData, loading: adminEhungryLoading, refetch: refetchAdminEhungry } = useQuery(ADMIN_EHUNGRY_ORDERS, adminLists)
  const [updateEhungryStatus, ehungryStatusState] = useMutation(EHUNGRY_UPDATE_STATUS)
  // createdAt mirrors placedAt purely so filterByDate, which every other tab
  // uses, reads these rows too instead of needing a parallel date filter.
  const ehungryOrders = ((isSuperAdmin ? adminEhungryData?.adminEhungryOrders : ehungryData?.ehungryOrders)?.orders ?? [])
    .map((o) => ({ ...o, createdAt: o.placedAt }))

  const [markPickupComplete, markPickupState] = useMutation(RESTAURANT_MARK_PICKUP_COMPLETE)
  const importedOrders = (isSuperAdmin ? adminCourierData?.courierOrders : courierOrdersData?.restaurantCourierOrders)?.orders ?? []
  // courierOrderId set means it already has a row above — skip to avoid showing
  // the same order twice. IGNORED means an admin already dismissed it.
  const pendingEmailOrders = ((isSuperAdmin ? adminCourierEmailsData?.courierEmails : courierEmailsData?.restaurantCourierEmails)?.emails ?? [])
    .filter((e) => !e.courierOrderId && e.status !== 'IGNORED')
    .map(normalizePendingEmail)
    .filter(Boolean)
  const emailOrders = [...importedOrders, ...pendingEmailOrders]

  const { data: staffListData } = useQuery(GET_RESTAURANT_STAFF, { variables: { restaurantId: restaurantId || null }, skip: isSuperAdmin })
  const { data: onDutyData, refetch: refetchOnDuty } = useQuery(GET_ON_DUTY_STAFF, { skip: isSuperAdmin })

  // Sync selectedOnDutyIds when on-duty data loads
  React.useEffect(() => {
    if (onDutyData?.onDutyStaff?.onDutyStaff) {
      setSelectedOnDutyIds(onDutyData.onDutyStaff.onDutyStaff.map(s => s._id))
    }
  }, [onDutyData])

  // ── Real-time subscription for restaurant admin ───────────────────
  useEffect(() => {
    if (isSuperAdmin || !restaurantId) return
    activeSubscribeToMore({
      document: SUBSCRIBE_PLACE_ORDER,
      variables: { restaurant: restaurantId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        const newOrder = subscriptionData.data.subscribePlaceOrder
        if (!newOrder) return prev
        const existing = (prev.getActiveOrders || []).findIndex(o => o._id === newOrder._id)
        if (existing === -1) {
          return { getActiveOrders: [newOrder, ...(prev.getActiveOrders || [])] }
        }
        const updated = [...prev.getActiveOrders]
        updated[existing] = newOrder
        return { getActiveOrders: updated }
      },
      onError: err => console.log('Subscription error:', err)
    })
  }, []) // run once on mount only

  // Keep selected order in sync when active orders data updates
  useEffect(() => {
    if (!selectedOrder || !activeOrdersData?.getActiveOrders) return
    const updated = activeOrdersData.getActiveOrders.find(o => o._id === selectedOrder._id)
    if (updated) setSelectedOrder(updated)
  }, [activeOrdersData]) // activeOrdersData is the only trigger needed here

  /* Reload what feeds the marketplace list. For the super admin that is the
     all-orders query: its getActiveOrders call cannot work (see liveViewOrders),
     so refetching only that left the super admin's screen stale after every
     Accept, Mark Ready or Refresh. */
  const refreshOrders = () => {
    Promise.resolve(refetchActive()).catch(() => {})
    if (isSuperAdmin) Promise.resolve(refetchAll()).catch(() => {})
  }

  // ── Mutations ─────────────────────────────────────────────────────
  const [mutateStatus] = useMutation(UPDATE_STATUS, {
    onCompleted: () => {
      showSnackbar('Order status updated')
      refreshOrders()
      refetchScheduled()
    },
    onError: err => showSnackbar(err.message, 'error')
  })
  const [mutateMarkPrepared] = useMutation(MARK_PREPARED, {
    onCompleted: () => {
      showSnackbar('Order marked ready')
      refreshOrders()
    },
    onError: err => showSnackbar(err.message, 'error')
  })

  const [mutateCancelOrder] = useMutation(UPDATE_ORDER_STATUS, {
    onCompleted: () => {
      showSnackbar('Order cancelled')
      refreshOrders()
    },
    onError: err => showSnackbar(err.message, 'error')
  })

  const [mutateAssignRider] = useMutation(ASSIGN_RIDER, {
    onCompleted: () => {
      showSnackbar('Rider assigned successfully')
      setRiderDialog(false)
      setAssigningOrder(null)
      setSelectedRider('')
      refreshOrders()
    },
    onError: err => showSnackbar(err.message, 'error')
  })

  const [mutateSetOnDuty, { loading: onDutyLoading }] = useMutation(SET_ON_DUTY_STAFF, {
    onCompleted: () => {
      showSnackbar('On-duty staff updated')
      setOnDutyPanelOpen(false)
      refetchOnDuty()
    },
    onError: err => showSnackbar(err.graphQLErrors?.[0]?.message || err.message, 'error')
  })

  // ── Derived data ──────────────────────────────────────────────────
  const activeOrders = (activeOrdersData?.getActiveOrders || []).filter(o => o.orderStatus !== 'PAYMENT_PENDING')
  const recentOrders = ((isSuperAdmin ? allData?.allOrders : restData?.ordersByRestId) || []).filter(o => o.orderStatus !== 'PAYMENT_PENDING')
  // The super admin has no scheduled-orders query (it needs one restaurant), so
  // its scheduled orders come from the all-orders list; before, none ever showed.
  const scheduledOrders = (isSuperAdmin
    ? recentOrders.filter(o => o.orderStatus === 'SCHEDULED')
    : (scheduledOrdersData?.getScheduledOrders || [])
  ).filter(o => o.orderStatus !== 'PAYMENT_PENDING')

  /* The super admin's open orders. getActiveOrders needs one restaurant and the
     admin's login token carries none, so for the super admin it errors on every
     poll and returns nothing — its Live View never showed a single new, accepted
     or on-the-way marketplace order. They come from the all-orders list instead,
     with that query's own rule (isActive). Anything getActiveOrders does return
     is kept first, as the fresher copy. */
  const openOrders = useMemo(() => {
    if (!isSuperAdmin) return activeOrders
    const seen = new Set(activeOrders.map(o => o._id))
    const fromAll = recentOrders.filter(o =>
      !['DELIVERED', 'CANCELLED'].includes(o.orderStatus) && o.isActive !== false && !seen.has(o._id)
    )
    return [...activeOrders, ...fromAll]
  }, [activeOrders, recentOrders])

  // Merge active + scheduled + terminal orders for the live view
  // (getActiveOrders excludes SCHEDULED and terminal statuses, so we supplement)
  const liveViewOrders = useMemo(() => {
    const scheduledIds = new Set(scheduledOrders.map(o => o._id))
    const uniqueActive = openOrders.filter(o => !scheduledIds.has(o._id))
    const allActiveIds = new Set([...scheduledOrders, ...uniqueActive].map(o => o._id))
    const terminalFromRecent = recentOrders.filter(o =>
      (o.orderStatus === 'DELIVERED' || o.orderStatus === 'CANCELLED') && !allActiveIds.has(o._id)
    )
    return [...scheduledOrders, ...uniqueActive, ...terminalFromRecent]
  }, [openOrders, scheduledOrders, recentOrders])

  const tableSubscribeToMore = isSuperAdmin ? allSubscribeToMore : restSubscribeToMore

  /* Which of the normal status tabs an eHungry order belongs in.
   *
   * They arrive already accepted — eHungry took them before we saw them — so
   * there is no New / Ready / Rider Out stage for them to sit in, and an open
   * one belongs with the work in hand: Accepted. Finished and cancelled ones
   * follow their own status rather than piling into Accepted too, because a
   * cancelled order listed as work is how a kitchen cooks something the
   * customer already called off. */
  const ehungryStatusTab = (order) =>
    order.status === 'cancelled' ? 'CANCELLED'
      : order.status === 'completed' ? 'DELIVERED'
        /* A pre-order waits in Scheduled until it is close enough to cook, then
           joins the work in hand — the same moment its ticket prints on the
           tablet, so screen and paper agree. */
        : isScheduledEhungryOrder(order) && Date.now() < ehungryDueAt(order) ? 'SCHEDULED'
          : 'ACCEPTED'

  /* Which tab an email/partner order belongs in, so it appears in All Orders
   * and in the status tabs instead of only in its own — the same mapping the
   * restaurant tablet uses, so both screens agree about where one order is.
   *
   * These also arrive already accepted: the partner took the order before we
   * saw it, so there is no New stage for them. Everything still in play sits in
   * Accepted; the ones First Delivery has moved on follow their own stage.
   *
   * null means "Email tab only" — NEEDS_REVIEW and anything unrecognised. Those
   * are not orders the kitchen can act on (mostly nothing matched a merchant),
   * and putting them in Accepted would ask someone to cook an order we have not
   * established is real. */
  const courierStatusTab = (order) => {
    switch (order.status) {
      case 'CANCELLED':
      case 'FAILED':
        return 'CANCELLED'
      case 'DELIVERED':
      case 'PICKED_UP':
        return 'DELIVERED'
      case 'EN_ROUTE':
        return 'PICKED'
      case 'ASSIGNED':
        return 'ASSIGNED'
      case 'SCHEDULED':
        return 'SCHEDULED'
      case 'DRAFT':
      case 'READY':
      case 'DISPATCHING':
        /* A pre-order waits in Scheduled until it is close enough to cook, then
           joins the work in hand — the same moment its ticket prints on the
           tablet, so screen and paper agree. Nothing ever sets the SCHEDULED
           status above (a pre-order rests in READY with a future dispatchAt),
           so the promise time is the only signal there is. */
        return isScheduledCourierOrder(order) && Date.now() < courierPrintDueAt(order)
          ? 'SCHEDULED'
          : 'ACCEPTED'
      default:
        return null
    }
  }

  /* ── The list: source × status × date range × search, then sorted ──
     Every number on the page — status pills and source pills — comes from
     listFor, so a count always matches what opening it shows. */
  const matchesSearch = useMemo(() => buildMatcher(orderSearch), [orderSearch])
  const pools = useMemo(() => {
    const ofType = o => typeFilter === 'ALL' ||
      (typeFilter === 'PHONE' ? o.orderSource === 'MANUAL' : fulfilmentOf(o) === typeFilter)
    const keep = list => (matchesSearch ? list.filter(matchesSearch) : list).filter(ofType)
    return {
      marketplace: keep(filterByDate(liveViewOrders, dateFilter, customRange)),
      // Tagged so the card renderer (and search) can tell the three apart once they share a list.
      ehungry: keep(filterByDate(ehungryOrders, dateFilter, customRange).map(o => ({ ...o, __ehungry: true }))),
      email: keep(filterByDate(emailOrders, dateFilter, customRange).map(o => ({ ...o, __courier: true })))
    }
  }, [liveViewOrders, ehungryOrders, emailOrders, dateFilter, customRange, matchesSearch, typeFilter])

  const listFor = (source, status) => {
    const out = []
    if (source === 'ALL' || source === 'MARKETPLACE') out.push(...filterByStatus(pools.marketplace, status))
    // Email and legacy-app orders carry no refunds of their own.
    if (status !== 'REFUNDED') {
      if (source === 'ALL' || source === 'EHUNGRY') {
        out.push(...pools.ehungry.filter(o => status === 'ALL' || ehungryStatusTab(o) === status))
      }
      if (source === 'ALL' || source === 'EMAIL') {
        /* With the Email source picked, All shows every email order, NEEDS_REVIEW
           ones included (courierStatusTab null) — what the old Email tab showed.
           Mixed with other sources, only the ones mapped to a real stage. */
        out.push(...pools.email.filter(o => (status === 'ALL'
          ? source === 'EMAIL' || courierStatusTab(o) !== null
          : courierStatusTab(o) === status)))
      }
    }
    return out
  }
  const countFor = (source, status) => listFor(source, status).length
  const visibleOrders = sortOrders(listFor(sourceFilter, statusFilter), sortBy)

  // Choosing Custom starts from today–today, ready to widen.
  const selectDateFilter = value => {
    setDateFilter(value)
    if (value === 'CUSTOM' && !customRange.from && !customRange.to) {
      const d = new Date()
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      setCustomRange({ from: today, to: today })
    }
  }

  // "/" jumps to the search box, as in most dashboards.
  const searchInputRef = React.useRef(null)
  useEffect(() => {
    const onKey = e => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const tag = (e.target.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return
      e.preventDefault()
      searchInputRef.current && searchInputRef.current.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ── Handlers ──────────────────────────────────────────────────────
  const handleViewDetail = order => {
    setSelectedOrder(order)
    setDetailModal(true)
  }

  /* Deep link from the Dashboard's Recent orders: #/admin/orders?open=o:12
     (marketplace), e:34 (legacy app) or c:56 (partner). Opens that order's
     detail once its list has loaded, then forgets the link so closing the
     modal does not reopen it. */
  const openedFromLinkRef = React.useRef(false)
  useEffect(() => {
    if (openedFromLinkRef.current) return
    const q = (window.location.hash.split('?')[1] || '')
    const open = new URLSearchParams(q).get('open')
    if (!open) { openedFromLinkRef.current = true; return }
    const [kind, id] = open.split(':')
    const byId = list => (list || []).find(o => String(o._id) === String(id))
    let hit = null
    if (kind === 'o') { hit = byId(recentOrders) || byId(liveViewOrders); if (hit) handleViewDetail(hit) }
    else if (kind === 'e') { hit = byId(ehungryOrders); if (hit) setSelectedEhungryOrder(hit) }
    else if (kind === 'c') { hit = byId(importedOrders); if (hit) setSelectedEmailOrder(hit) }
    if (hit) {
      openedFromLinkRef.current = true
      window.history.replaceState(null, '', window.location.href.replace(/\?open=[^&#]*/, ''))
    }
  }, [recentOrders, liveViewOrders, ehungryOrders, importedOrders])

  const handleAccept = order => {
    mutateStatus({ variables: { id: order._id, orderStatus: 'ACCEPTED' } })
  }

  const handleMarkPrepared = order => {
    mutateMarkPrepared({ variables: { id: order._id } })
  }

  const handleCancel = order => {
    setCancellingOrder(order)
    setCancelReason('')
    setCancelDialog(true)
  }

  const handleConfirmCancel = () => {
    if (!cancelReason) {
      showSnackbar('Please select a cancellation reason', 'warning')
      return
    }
    mutateCancelOrder({
      variables: { id: cancellingOrder._id, status: 'CANCELLED', reason: cancelReason, reasonComments: cancelComments.trim() || undefined }
    })
    setCancelDialog(false)
    setCancellingOrder(null)
    setCancelReason('')
    setCancelComments('')
  }

  const handleAssignRider = order => {
    setAssigningOrder(order)
    setSelectedRider('')
    setRiderDialog(true)
  }

  const handleConfirmAssignRider = () => {
    if (!selectedRider) {
      showSnackbar('Please select a rider', 'warning')
      return
    }
    mutateAssignRider({ variables: { id: assigningOrder._id, riderId: selectedRider } })
  }

  // A pending card's _id is synthesized as `email-<id>` (see normalizePendingEmail)
  // since it has no order row yet — that's the one signal telling this handler
  // whether to pass emailId (import it first, server-side) or orderId (already
  // has a row, just flip its status).
  const handleMarkPickedUp = async (order, markPaid) => {
    const idStr = String(order._id)
    const isPending = idStr.startsWith('email-')
    const variables = isPending
      ? { emailId: idStr.slice('email-'.length), restaurantId: restaurantId || null, markPaid }
      : { orderId: order._id, restaurantId: restaurantId || null, markPaid }
    try {
      await markPickupComplete({ variables })
      showSnackbar(markPaid ? 'Marked paid and picked up' : 'Marked picked up')
      setSelectedEmailOrder(null)
      if (isSuperAdmin) { refetchAdminCourierOrders(); refetchAdminCourierEmails() } else { refetchCourierOrders(); refetchCourierEmails() }
    } catch (e) {
      showSnackbar(e.message, 'error')
    }
  }

  /**
   * Change an eHungry order's status. This writes through to the legacy
   * RushMyFood app, so the error is shown inside the dialog rather than only in
   * a snackbar that disappears — a refused write-through needs to stay readable
   * next to the order it refers to.
   */
  const handleEhungryStatusChange = async (order, status) => {
    setEhungryError(null)
    try {
      const res = await updateEhungryStatus({ variables: { id: order._id, status } })
      const saved = res?.data?.ehungryUpdateOrderStatus
      showSnackbar(`Order #${order.remoteId} marked ${status}`)
      // Reflect the status the legacy app actually saved, not the one requested.
      setSelectedEhungryOrder((prev) => (prev && saved ? { ...prev, ...saved } : prev))
      if (isSuperAdmin) refetchAdminEhungry(); else refetchEhungry()
    } catch (e) {
      setEhungryError(e.message)
      showSnackbar(e.message, 'error')
    }
  }

  /* A brief glow on an order that arrives by live update. Visual only: refs,
     never state, so it cannot cause a render of its own. Orders present on the
     first load never glow — only ones first seen after the page has settled,
     and placed in the last 15 minutes. */
  const mountedAtRef = React.useRef(Date.now())
  const seenRef = React.useRef(new Set())
  const glowUntilRef = React.useRef(new Map())
  const noteArrivals = (list, prefix) => {
    const now = Date.now()
    for (const o of list) {
      const key = prefix + o._id
      if (seenRef.current.has(key)) continue
      seenRef.current.add(key)
      if (now - mountedAtRef.current > 8000 && now - new Date(o.createdAt).getTime() < 15 * 60000) {
        glowUntilRef.current.set(key, now + 2400)
      }
    }
  }
  noteArrivals(liveViewOrders, 'o:')
  noteArrivals(ehungryOrders, 'e:')
  noteArrivals(emailOrders, 'c:')
  const isArrival = key => (glowUntilRef.current.get(key) || 0) > Date.now()

  // Skeletons while the source being shown is still loading.
  const listLoading = sourceFilter === 'EHUNGRY'
    ? (isSuperAdmin ? adminEhungryLoading : ehungryLoading) && ehungryOrders.length === 0
    : sourceFilter === 'EMAIL'
      ? (isSuperAdmin ? adminCourierLoading && !adminCourierData : courierOrdersLoading)
      // The super admin's marketplace orders come from the all-orders query.
      : (isSuperAdmin ? allLoading && !allData : activeLoading)

  // What a click on a card or row opens, by kind of order.
  const openOrder = order =>
    order.__ehungry ? setSelectedEhungryOrder(order)
      : order.__courier ? setSelectedEmailOrder(order)
        : handleViewDetail(order)

  const emptyState = (
    orderSearch.trim() ? (
      <EmptyState
        icon={<SearchRoundedIcon />}
        title={`No orders match “${orderSearch.trim()}”`}
        message={dateFilter !== 'ALL'
          ? 'Only this date range was searched. Search all time, or check the order number, customer or restaurant name.'
          : 'Search by order number, customer name or phone, or restaurant.'}
        action={
          <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
            {dateFilter !== 'ALL' && (
              <Button size="small" variant="contained" disableElevation onClick={() => setDateFilter('ALL')} sx={{ ...btn.primary, px: 1.75 }}>
                Search all time
              </Button>
            )}
            <Button size="small" onClick={() => setOrderSearch('')} sx={{ ...btn.secondary, px: 1.75 }}>
              Clear search
            </Button>
          </Box>
        }
      />
    ) : sourceFilter === 'EHUNGRY' && statusFilter === 'ALL' ? (
      <EmptyState
        icon={<StorefrontOutlinedIcon />}
        title="No RushMyFood (old) orders found"
        message={dateFilter !== 'ALL'
          ? 'Try selecting a wider date range'
          : 'Orders from the old RushMyFood system appear here once a super admin links this restaurant to it'}
      />
    ) : sourceFilter === 'EMAIL' && statusFilter === 'ALL' ? (
      <EmptyState
        icon={<MailOutlineIcon />}
        title="No email orders found"
        message={dateFilter !== 'ALL'
          ? 'Try selecting a wider date range'
          : 'Orders forwarded from your other online-ordering platform will show up here'}
      />
    ) : (
      <EmptyState
        icon={<InboxOutlinedIcon />}
        title="No orders found"
        message={statusFilter !== 'ALL'
          ? 'Try selecting a different status filter'
          : dateFilter !== 'ALL'
          ? 'Try selecting a wider date range'
          : 'No active orders at this time'}
        action={
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => refreshOrders()}
            sx={{ ...btn.secondary, px: 1.75 }}
          >
            Refresh
          </Button>
        }
      />
    )
  )

  // ── Render ────────────────────────────────────────────────────────
  return (
    // flow-root keeps <Header />'s negative top margin inside this box, so the
    // page colour runs to the bottom of the screen.
    <Box sx={{ ...opsCssVars, bgcolor: ops.page, minHeight: '100vh', display: 'flow-root' }}>
      <Header />
      <Container maxWidth={false} sx={{ mt: { xs: 2, sm: 4 }, pb: 5, px: { xs: 2, md: 3 } }}>

        {/* Page Title + Controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1.5}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              component="h1"
              sx={{ fontSize: FS.xxl, fontWeight: 600, letterSpacing: '-0.02em', color: ops.text, lineHeight: 1.2 }}
            >
              Orders
            </Typography>
            <Box
              display="inline-flex"
              alignItems="center"
              gap={0.75}
              sx={{
                height: 24,
                px: 1.1,
                borderRadius: RADIUS.chip,
                bgcolor: tone('emerald').bg,
                border: '1px solid rgba(23,178,106,0.28)'
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: tone('emerald').dot,
                  animation: `${pulse} 2s ease-out infinite`,
                  ...noMotion
                }}
              />
              <Typography component="span" sx={{ fontSize: FS.label, color: tone('emerald').fg, fontWeight: 700, letterSpacing: '0.08em' }}>
                LIVE
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Refresh">
              <IconButton
                aria-label="Refresh"
                onClick={() => refreshOrders()}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: RADIUS.button,
                  border: `1px solid ${ops.borderStrong}`,
                  bgcolor: ops.surface,
                  color: ops.text2,
                  boxShadow: SHADOW.pop,
                  '&:hover': { bgcolor: ops.subtle },
                  // Spins once on release; CSS only, the click still just refetches.
                  '& svg': { transition: `transform 600ms ${EASE}` },
                  '&:active svg': { transform: 'rotate(-360deg)', transition: 'transform 0s' },
                  ...focusRing,
                  '@media (prefers-reduced-motion: reduce)': {
                    '& svg': { transition: 'none' },
                    '&:active svg': { transform: 'none' }
                  }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                bgcolor: ops.sunken,
                p: '3px',
                gap: '2px',
                borderRadius: RADIUS.button,
                '& .MuiToggleButton-root': {
                  border: '0 !important',
                  borderRadius: '8px !important',
                  marginLeft: '0 !important',
                  height: 30,
                  px: 1.5,
                  gap: 0.75,
                  textTransform: 'none',
                  fontFamily: 'inherit',
                  fontSize: FS.sm,
                  fontWeight: 500,
                  color: ops.muted,
                  '& svg': { fontSize: 16 },
                  '&:hover': { bgcolor: 'transparent', color: ops.text },
                  '&.Mui-selected': {
                    bgcolor: ops.surface,
                    color: ops.text,
                    fontWeight: 600,
                    boxShadow: '0 1px 2px rgba(16,24,40,.08), 0 0 0 1px rgba(16,24,40,.04)',
                    '&:hover': { bgcolor: ops.surface }
                  },
                  '&.Mui-focusVisible': { outline: `2px solid ${ops.focus}`, outlineOffset: 2 }
                }
              }}
            >
              <ToggleButton value="live">
                <ViewModuleOutlinedIcon />
                Live View
              </ToggleButton>
              <ToggleButton value="table">
                <TableChartOutlinedIcon />
                Column View
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* ── On Duty Staff Panel (restaurant admin only) ───────── */}
        {!isSuperAdmin && (
          <Box mb={3}>
            <Paper
              elevation={0}
              sx={{
                bgcolor: ops.surface,
                border: `1px solid ${ops.border}`,
                borderRadius: RADIUS.card,
                boxShadow: SHADOW.card,
                overflow: 'hidden'
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={2}
                py={1.5}
                sx={{ cursor: 'pointer', transition: `background-color 150ms ${EASE}`, '&:hover': { bgcolor: ops.subtle } }}
                onClick={() => setOnDutyPanelOpen(o => !o)}
              >
                <Box display="flex" alignItems="center" gap={1.25} minWidth={0} flexWrap="wrap">
                  <Box
                    aria-hidden
                    sx={{
                      width: 28, height: 28, borderRadius: '8px', bgcolor: ops.sunken, color: ops.text2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 16 }
                    }}
                  >
                    <PeopleAltOutlinedIcon />
                  </Box>
                  <Typography sx={{ fontSize: FS.md, fontWeight: 600, color: ops.text }}>
                    On Duty Staff
                  </Typography>
                  {onDutyData?.onDutyStaff?.onDutyStaff?.length > 0 && (
                    <Chip
                      size="small"
                      label={onDutyData.onDutyStaff.onDutyStaff.map(s => `${s.firstName} ${s.lastName}`).join(', ')}
                      sx={{ bgcolor: tone('emerald').bg, color: tone('emerald').fg, fontWeight: 600, fontSize: FS.xs, height: 24, borderRadius: RADIUS.chip, maxWidth: '100%' }}
                    />
                  )}
                  {!onDutyData?.onDutyStaff?.onDutyStaff?.length && (
                    <Chip size="small" label="No one set" sx={{ bgcolor: tone('amber').bg, color: tone('amber').fg, fontWeight: 600, fontSize: FS.xs, height: 24, borderRadius: RADIUS.chip }} />
                  )}
                </Box>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: ops.muted,
                    flexShrink: 0,
                    transform: onDutyPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: `transform 200ms ${EASE}`
                  }}
                />
              </Box>
              <Collapse in={onDutyPanelOpen}>
                <Box px={2} pt={1.5} pb={2} sx={{ borderTop: `1px solid ${ops.border}` }}>
                  <Typography sx={{ fontSize: FS.sm, color: ops.muted, display: 'block', mb: 1.5 }}>
                    Select which staff members are working this shift. New orders will be auto-assigned to them in round-robin.
                  </Typography>
                  {(staffListData?.restaurantStaff || []).length === 0 && (
                    <Typography sx={{ fontSize: FS.md, color: ops.muted }}>No active staff members. Add staff in the Staff page.</Typography>
                  )}
                  <Grid container spacing={1} mb={1.5}>
                    {(staffListData?.restaurantStaff || []).map(s => (
                      <Grid item xs={12} sm={6} md={4} key={s._id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedOnDutyIds.includes(s._id)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setSelectedOnDutyIds(ids => [...ids, s._id])
                                } else {
                                  setSelectedOnDutyIds(ids => ids.filter(id => id !== s._id))
                                }
                              }}
                              size="small"
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: FS.md, color: ops.text }}>
                              {s.firstName} {s.lastName}
                              <Typography component="span" sx={{ fontSize: FS.xs, color: ops.muted, ml: 0.5 }}>({s.role})</Typography>
                            </Typography>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    disabled={onDutyLoading}
                    sx={{ ...btn.primary, px: 2 }}
                    onClick={() => mutateSetOnDuty({ variables: { staffIds: selectedOnDutyIds } })}
                  >
                    Save On Duty Staff
                  </Button>
                </Box>
              </Collapse>
            </Paper>
          </Box>
        )}

        {/* ── Filters: one bar for both views ─────────────────── */}
        {/* Filters */}
        <Box mb={2.5} display="flex" flexDirection="column" gap={1.5}>
          {/* Search, sort and date range */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <TextField
              inputRef={searchInputRef}
              value={orderSearch}
              onChange={e => setOrderSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOrderSearch('') }}
              placeholder="Search order #, customer or restaurant"
              size="small"
              inputProps={{ 'aria-label': 'Search orders by order number, customer or restaurant' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 18, color: ops.muted }} />
                  </InputAdornment>
                ),
                endAdornment: orderSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear search" onClick={() => setOrderSearch('')} sx={{ p: 0.5 }}>
                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <Box
                      component="kbd"
                      aria-hidden
                      sx={{
                        fontFamily: 'inherit', fontSize: FS.label, fontWeight: 600, color: ops.muted,
                        border: `1px solid ${ops.border}`, borderRadius: '5px', px: 0.6, lineHeight: '16px'
                      }}
                    >
                      /
                    </Box>
                  </InputAdornment>
                ),
                sx: {
                  height: 36,
                  borderRadius: RADIUS.button,
                  bgcolor: ops.surface,
                  fontSize: FS.sm,
                  boxShadow: SHADOW.pop,
                  '& fieldset': { borderColor: ops.border },
                  '&:hover fieldset': { borderColor: `${ops.borderStrong} !important` },
                  '&.Mui-focused fieldset': { borderColor: `${ops.focus} !important`, borderWidth: '1px !important' },
                  '& input::placeholder': { color: ops.muted, opacity: 1 }
                }
              }}
              sx={{ flex: '1 1 280px', maxWidth: { xs: '100%', md: 420 } }}
            />

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Select
                size="small"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                inputProps={{ 'aria-label': 'Sort orders' }}
                renderValue={v => (
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <SwapVertRoundedIcon sx={{ fontSize: 17, color: ops.muted }} />
                    {SORTS.find(o => o.value === v)?.label}
                  </Box>
                )}
                sx={{
                  height: 34,
                  borderRadius: RADIUS.button,
                  bgcolor: ops.surface,
                  fontSize: FS.sm,
                  fontWeight: 500,
                  color: ops.text2,
                  boxShadow: SHADOW.pop,
                  '& .MuiSelect-select': { color: ops.text2, py: 0.5 },
                  '& fieldset': { borderColor: ops.border },
                  '&:hover fieldset': { borderColor: `${ops.borderStrong} !important` },
                  '&.Mui-focused fieldset': { borderColor: `${ops.focus} !important`, borderWidth: '1px !important' }
                }}
                MenuProps={{ PaperProps: { sx: { borderRadius: RADIUS.button, mt: 0.5, boxShadow: SHADOW.cardHover } } }}
              >
                {SORTS.map(o => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: FS.sm }}>{o.label}</MenuItem>
                ))}
              </Select>

              <Segmented
                label="Order type"
                options={TYPE_FILTERS}
                value={typeFilter}
                onSelect={value => setTypeFilter(value)}
              />

              <Segmented
                label="Date range"
                options={DATE_FILTERS}
                value={dateFilter}
                onSelect={value => selectDateFilter(value)}
              />
            </Box>
          </Box>

          {/* Custom date range */}
          {dateFilter === 'CUSTOM' && (
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} flexWrap="wrap">
              <Typography component="span" sx={{ ...labelSx, mr: 0.25 }}>Custom range</Typography>
              {[['from', 'From'], ['to', 'To']].map(([key, label]) => (
                <TextField
                  key={key}
                  type="date"
                  size="small"
                  label={label}
                  value={customRange[key]}
                  onChange={e => setCustomRange(r => ({ ...r, [key]: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: key === 'from' ? customRange.to || undefined : undefined, min: key === 'to' ? customRange.from || undefined : undefined }}
                  InputProps={{ sx: { height: 36, borderRadius: RADIUS.button, bgcolor: ops.surface, fontSize: FS.sm, ...tabular } }}
                  sx={{ width: 170 }}
                />
              ))}
            </Box>
          )}

          {/* Status pills — counts follow the source, date range and search */}
          <ScrollRow label="Filter by status">
            {STATUS_TABS.map(tab => (
              <FilterPill
                key={tab.value}
                active={statusFilter === tab.value}
                dot={ORDER_TONE[tab.value] ? tone(ORDER_TONE[tab.value]).dot : null}
                label={tab.label}
                count={countFor(sourceFilter, tab.value)}
                onClick={() => setStatusFilter(tab.value)}
              />
            ))}
          </ScrollRow>

          {/* Source pills left, how many are showing right */}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" minWidth={0} role="group" aria-label="Filter by source">
              <Typography component="span" sx={{ ...labelSx, mr: 0.5 }}>Source</Typography>
              {SOURCE_TABS.map(src => (
                <FilterPill
                  key={src.value}
                  active={sourceFilter === src.value}
                  icon={SOURCE_ICONS[src.value]}
                  label={src.label}
                  count={countFor(src.value, statusFilter)}
                  onClick={() => setSourceFilter(src.value)}
                />
              ))}
            </Box>
            <Typography aria-live="polite" sx={{ fontSize: FS.sm, color: ops.muted, ...tabular }}>
              {visibleOrders.length} order{visibleOrders.length === 1 ? '' : 's'}
              {orderSearch.trim() ? ` matching “${orderSearch.trim()}”` : ''}
            </Typography>
          </Box>
        </Box>


        {/* ── Live View (cards) or Column View (rows): the same list ── */}
        {viewMode === 'live' ? (
          listLoading ? (
            <SkeletonGrid />
          ) : visibleOrders.length === 0 ? (
            emptyState
          ) : (
            <Box sx={cardGridSx}>
              {/* Namespaced keys: marketplace, eHungry and courier ids are all
                  plain integers, so order 6 from two of them would share a key
                  and React would reuse one card for the other. */}
              {visibleOrders.map((order, i) => (
                <Box key={order.__ehungry ? `eh-${order._id}` : order.__courier ? `em-${order._id}` : order._id} sx={cellSx(i)}>
                  {order.__ehungry ? (
                    <EhungryOrderCard
                      order={order}
                      onViewDetail={setSelectedEhungryOrder}
                      currencySymbol={currencySymbol}
                      courierName="RushMyFood"
                    />
                  ) : order.__courier ? (
                    <EmailOrderCard
                      order={order}
                      onViewDetail={setSelectedEmailOrder}
                      currencySymbol={currencySymbol}
                    />
                  ) : (
                  <OrderCard
                    order={order}
                    onViewDetail={handleViewDetail}
                    onAccept={handleAccept}
                    onMarkPrepared={handleMarkPrepared}
                    onCancel={handleCancel}
                    onAssignRider={handleAssignRider}
                    isSuperAdmin={isSuperAdmin}
                    currencySymbol={currencySymbol}
                  />
                  )}
                  {isArrival((order.__ehungry ? 'e:' : order.__courier ? 'c:' : 'o:') + order._id) && <Box sx={arrivalSx} />}
                </Box>
              ))}
            </Box>
          )
        ) : (
          <OrdersTable
            orders={visibleOrders}
            loading={listLoading}
            isSuperAdmin={isSuperAdmin}
            currencySymbol={currencySymbol}
            sortBy={sortBy}
            onSort={setSortBy}
            resetKey={[sourceFilter, statusFilter, typeFilter, dateFilter, customRange.from, customRange.to, orderSearch, sortBy].join('|')}
            onOpen={openOrder}
            selected={selectedOrder}
            updateSelected={setSelectedOrder}
            subscribeToMore={tableSubscribeToMore}
            empty={emptyState}
          />
        )}
      </Container>

      {/* ── Order Detail Modal ────────────────────────────────── */}
      <Modal
        open={detailsModal}
        onClose={() => {
          setDetailModal(false)
          setSelectedOrder(null)
        }}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
        slotProps={{ backdrop: { sx: backdropSx } }}
      >
        <Box
          sx={{
            bgcolor: ops.surface,
            borderRadius: RADIUS.modal,
            boxShadow: SHADOW.modal,
            width: '100%',
            maxWidth: 720,
            maxHeight: '90vh',
            overflowY: 'auto',
            outline: 'none',
            animation: `${modalIn} 180ms ${EASE}`,
            ...noMotion
          }}
        >
          <OrderComponent
            order={selectedOrder}
            onClose={() => setDetailModal(false)}
            onStatusChange={() => refreshOrders()}
            availableRiders={ridersData?.availableRiders || []}
          />
        </Box>
      </Modal>

      <EmailOrderDetail
        order={selectedEmailOrder}
        onClose={() => setSelectedEmailOrder(null)}
        onMarkPickedUp={handleMarkPickedUp}
        markingPickedUp={markPickupState.loading}
      />

      <EhungryOrderDetail
        order={selectedEhungryOrder}
        onClose={() => { setSelectedEhungryOrder(null); setEhungryError(null) }}
        onChangeStatus={handleEhungryStatusChange}
        changingStatus={ehungryStatusState.loading}
        error={ehungryError}
        courierName="RushMyFood"
      />

      {/* ── Rider Assignment Dialog ───────────────────────────── */}
      <Dialog
        open={riderDialog}
        onClose={() => setRiderDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
        BackdropProps={{ sx: backdropSx }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: FS.xl, color: ops.text, ...tabular }}>
          Assign Rider — #{assigningOrder?.orderId}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: FS.md, color: ops.muted, mb: 2 }}>
            Select an available rider to deliver this order.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select Rider</InputLabel>
            <Select
              value={selectedRider}
              label="Select Rider"
              onChange={e => setSelectedRider(e.target.value)}
              sx={{ borderRadius: RADIUS.button }}
            >
              {(ridersData?.availableRiders || []).map(rider => (
                <MenuItem key={rider._id} value={rider._id}>
                  {rider.name}
                  {rider.phone && (
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1, ...tabular }}>
                      {rider.phone}
                    </Typography>
                  )}
                </MenuItem>
              ))}
              {!(ridersData?.availableRiders?.length) && (
                <MenuItem disabled>No available riders right now</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setRiderDialog(false)} sx={{ ...btn.secondary, px: 2 }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleConfirmAssignRider}
            disabled={!selectedRider}
            sx={{ ...btn.solid('#026AA2', '#065986'), px: 2, '&.Mui-disabled': { bgcolor: ops.sunken, color: ops.faint } }}
          >
            Assign Rider
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Order Dialog ───────────────────────────────── */}
      <Dialog
        open={cancelDialog}
        onClose={() => { setCancelDialog(false); setCancelReason(''); setCancelComments('') }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
        BackdropProps={{ sx: backdropSx }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: FS.xl, color: tone('rose').fg, ...tabular }}>
          Cancel Order — #{cancellingOrder?.orderId}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Cancellation Reason *</InputLabel>
            <Select
              value={cancelReason}
              label="Cancellation Reason *"
              onChange={e => setCancelReason(e.target.value)}
              sx={{ borderRadius: RADIUS.button }}
            >
              <MenuItem value="CustomerCancelled">Customer Cancelled — Customer no longer wants the order</MenuItem>
              <MenuItem value="CustomerChangedOrder">Customer Changed Order — Customer revised the order</MenuItem>
              <MenuItem value="MissedDropoffTime">Missed Dropoff Time — Delivery delayed and not arrived</MenuItem>
              <MenuItem value="CustomerOtherReason">Customer Other Reason</MenuItem>
              <MenuItem value="DriverMissedPickup">Driver Missed Pickup — Driver delayed arriving to pickup</MenuItem>
              <MenuItem value="UnableToFulfill">Unable To Fulfill — Out of stock or store closed</MenuItem>
              <MenuItem value="VendorOtherReason">Vendor Other Reason</MenuItem>
              <MenuItem value="SystemFailure">System Failure — POS or payment gateway failure</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Additional comments (optional)"
            placeholder="Any extra details..."
            value={cancelComments}
            onChange={e => setCancelComments(e.target.value)}
            multiline
            rows={2}
            InputProps={{ sx: { borderRadius: RADIUS.button } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => { setCancelDialog(false); setCancelReason(''); setCancelComments('') }} sx={{ ...btn.secondary, px: 2 }}>Go Back</Button>
          <Button variant="contained" disableElevation onClick={handleConfirmCancel} sx={{ ...btn.solid('#C01048', '#A11043'), px: 2 }}>
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ──────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ borderRadius: RADIUS.button }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default withTranslation()(Orders)
